import { useState } from 'react'

import { Conditions, Fields, Operands, RuleCreationSource } from '@root/constants'
import { useBusinessContext } from '@root/context'
import { DEFAULT_ACCRUAL_DATE_MODE } from '@root/pages/SettingsPage/sections/AutomationSettings/AccountingRule/Form/accrualDateMode'
import {
  fetchAccount,
  IAccount,
  IAccountingRule,
  IAccountingRuleBlueprintEntry,
  IBlueprintEntry,
  IBusiness,
  ICountryConfig,
  ImportDataSource,
  Rule
} from '@root/query'
import { IAccountingSuggestion } from '@root/query/suggestions'
import { getVatRateLabel } from '@root/utils/getVatOptions'

import { useQueryClient } from '@tanstack/react-query'
import dayjs, { Dayjs } from 'dayjs'

interface Return {
  isLoading: boolean
  getDefaultRule: (contact?: number | null) => Promise<Partial<IAccountingRule> | undefined>
}

const mapEntriesForRule = (
  entries: IBlueprintEntry[],
  countryConfig: ICountryConfig,
  suggestionDate: Dayjs
) => {
  return entries.map(entry => ({
    account_id: entry.account_id,
    amount: 's',
    default_vat_code: entry.vat_code,
    default_vat_rate_label: getVatRateLabel(countryConfig, entry.vat_rate, suggestionDate)
  }))
}

const sourceAccountForRule = (
  suggestion: IAccountingSuggestion,
  sourceAccountData: IAccount,
  business: IBusiness,
  suggestionDate: Dayjs
) => {
  return {
    account_id: suggestion.source_account.id,
    amount: 's',
    default_vat_code: sourceAccountData?.default_vat_code,
    default_vat_rate_label: getVatRateLabel(
      business.country_config,
      sourceAccountData?.default_vat_rate,
      suggestionDate
    )
  }
}

const TENS_ROUNDING = 10
const HUNDREDS_ROUNDING = 100
const THOUSANDS_ROUNDING = 1000

const getMinAndMaxThresholds = (amount: number): { min: number; max: number } => {
  if (amount === 0) {
    return { min: 0, max: 0 }
  }

  const absAmount = Math.abs(amount)

  const variance = absAmount * 0.2

  let min = absAmount - variance
  let max = absAmount + variance

  if (absAmount < 1) {
    min = 1
    max = 2
  } else if (absAmount < 10) {
    min = Math.round(min)
    max = Math.round(max)
  } else if (absAmount < 200) {
    min = Math.floor((min + 1) / TENS_ROUNDING) * TENS_ROUNDING
    max = Math.ceil((max + 1) / TENS_ROUNDING) * TENS_ROUNDING
  } else if (absAmount < 2000) {
    min = Math.floor((min + 1) / HUNDREDS_ROUNDING) * HUNDREDS_ROUNDING
    max = Math.ceil((max + 1) / HUNDREDS_ROUNDING) * HUNDREDS_ROUNDING
  } else {
    min = Math.floor((min + 1) / THOUSANDS_ROUNDING) * THOUSANDS_ROUNDING
    max = Math.ceil((max + 1) / THOUSANDS_ROUNDING) * THOUSANDS_ROUNDING
  }

  // Getting the correct sign for
  // min and max based on the original
  // amount sign.
  if (amount < 0) {
    const temp = min
    min = -max
    max = -temp
  }

  return { min, max }
}

const getRulesFromSuggestion = (
  suggestion: IAccountingSuggestion,
  contact: number | null
): Rule[] => {
  const rules: Rule[] = []

  if (contact != null || (suggestion.contact == null && suggestion.contact_name?.trim())) {
    rules.push({
      field: Fields.CONTACT,
      condition: Conditions.CONTACT_IS_IN,
      value: contact != null ? [contact] : []
    })
  }

  if (suggestion?.import_data?.amount !== undefined) {
    const { min, max } = getMinAndMaxThresholds(suggestion.import_data.amount)

    rules.push(
      {
        field: Fields.MONEY,
        condition: Conditions.GREATER_THAN_EQUAL,
        value: min
      },
      {
        field: Fields.MONEY,
        condition: Conditions.LESS_THAN_EQUAL,
        value: max
      }
    )
  }

  return rules
}

export const useFillRuleFromSuggestion = (suggestion: IAccountingSuggestion): Return => {
  const { slug, data: business } = useBusinessContext()
  const queryClient = useQueryClient()
  const [isLoading, setIsLoading] = useState(false)

  // The form filling is done in this function
  // to reduce unnecessary fetches when navigating
  // the suggestions. Getting the source account data
  // only when needed.
  const getDefaultRule = async (contact: number | null = suggestion.contact ?? null) => {
    setIsLoading(true)
    try {
      const sourceAccountId = suggestion.source_account?.id
      const sourceAccountData = sourceAccountId
        ? await queryClient.fetchQuery({
            queryKey: [slug, 'accounts', sourceAccountId],
            queryFn: () => fetchAccount({ slug, accountId: sourceAccountId }),
            staleTime: 1000 * 60 * 5 // 5 minutes
          })
        : undefined

      const suggestionDate = dayjs(suggestion.date)

      // 1. Creating the debet and credit entries from the suggestions blueprint.
      const debetEntries: Partial<IAccountingRuleBlueprintEntry[]> = mapEntriesForRule(
        suggestion.blueprint.debet_entries || [],
        business.country_config,
        suggestionDate
      )

      const creditEntries: Partial<IAccountingRuleBlueprintEntry[]> = mapEntriesForRule(
        suggestion.blueprint.credit_entries || [],
        business.country_config,
        suggestionDate
      )

      // 2. Adding the source account to the entries aswell.
      if (debetEntries.length === 0) {
        debetEntries.push(
          sourceAccountForRule(suggestion, sourceAccountData, business, suggestionDate)
        )
      }

      if (creditEntries.length === 0) {
        creditEntries.push(
          sourceAccountForRule(suggestion, sourceAccountData, business, suggestionDate)
        )
      }

      const isPurchaseInvoiceAccrual =
        Boolean(business.has_accrual_based_entries_for_invoicing) &&
        suggestion.import_data?.data_source === ImportDataSource.PURCHASE_INVOICE

      return {
        enabled: true,
        name: '',
        description: suggestion.description || '',
        contact_name:
          contact == null && suggestion.contact == null
            ? suggestion.contact_name?.trim() || undefined
            : undefined,
        payment_account: isPurchaseInvoiceAccrual ? null : suggestion.source_account?.id || null,
        accrual_date_mode: isPurchaseInvoiceAccrual ? DEFAULT_ACCRUAL_DATE_MODE : null,
        created_from: RuleCreationSource.USER,
        rule_node: {
          operand: Operands.AND,
          rules: getRulesFromSuggestion(suggestion, contact)
        },
        blueprint: {
          debet_entries: debetEntries,
          credit_entries: creditEntries
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  return {
    isLoading,
    getDefaultRule
  }
}
